import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, FileDown } from "lucide-react";

const empty = {
  name: "",
  company: "",
  mobile: "",
  email: "",
  product_name: "",
  quantity: "",
  requirement: "",
};

export default function RFQModal({ open, onOpenChange, prefill = {} }) {
  const [form, setForm] = useState({ ...empty, ...prefill });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // sync prefill when modal opens
  const handleOpenChange = (v) => {
    if (v) setForm({ ...empty, ...prefill });
    if (!v) {
      setDone(null);
    }
    onOpenChange(v);
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/rfq", form);
      setDone(data);
      toast.success("Quotation request submitted. We'll get back to you within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl rounded-none p-0 border border-slate-200" data-testid="rfq-modal">
        <div className="bg-slate-900 text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight text-white">
              Request for Quotation
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Tell us about your requirement. Our engineering team responds within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 mx-auto bg-amber-500 grid place-items-center mb-4">
              <FileDown className="w-7 h-7 text-slate-900" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">Request received</h3>
            <p className="text-sm text-slate-600 mb-5">
              Reference No: <span className="font-mono">RFQ-{done.id.slice(0, 8).toUpperCase()}</span>
            </p>
            {done.pdf_url && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${done.pdf_url}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rfq-download-pdf"
                className="inline-flex items-center gap-2 px-5 h-10 btn-steel font-semibold text-sm"
              >
                <FileDown className="w-4 h-4" /> Download Quotation PDF
              </a>
            )}
            <div className="mt-6">
              <Button onClick={() => handleOpenChange(false)} className="rounded-none">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rfq-form">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Full Name *</Label>
              <Input data-testid="rfq-name-input" required value={form.name} onChange={setField("name")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Company</Label>
              <Input data-testid="rfq-company-input" value={form.company} onChange={setField("company")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Mobile *</Label>
              <Input data-testid="rfq-mobile-input" required value={form.mobile} onChange={setField("mobile")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Email *</Label>
              <Input data-testid="rfq-email-input" type="email" required value={form.email} onChange={setField("email")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Product / Service</Label>
              <Input data-testid="rfq-product-input" value={form.product_name} onChange={setField("product_name")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Quantity</Label>
              <Input data-testid="rfq-qty-input" value={form.quantity} onChange={setField("quantity")} className="rounded-none" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">Requirement Details *</Label>
              <Textarea data-testid="rfq-requirement-input" required rows={4} value={form.requirement} onChange={setField("requirement")} className="rounded-none" />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2 mt-2">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} className="rounded-none">
                Cancel
              </Button>
              <Button data-testid="rfq-submit-btn" type="submit" disabled={submitting} className="rounded-none btn-amber h-10 px-6 font-semibold">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit Request
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
